export type InsertPosition =
  | "beforebegin"
  | "afterbegin"
  | "beforeend"
  | "afterend";

type DataValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Event
  | DataObject
  | DataArray;
interface DataObject {
  [key: string]: DataValue;
}
type DataArray = Array<DataValue>;

type EventHandler = (event: Event) => void;

export class FrameCore {
  protected readonly scopeElement: Element;
  protected data: DataObject = {};

  private eventHandlers: Map<Element, EventHandler> = new Map();

  // 新增：保存原始模板内容
  private originalTemplates: Map<Node, string> = new Map();
  // 新增：保存属性节点的原始模板
  private originalAttributeTemplates: Map<Element, Map<string, string>> =
    new Map();

  // 新增：跟踪拖拽状态
  private dragState: {
    element: Element | null;
    startX: number;
    startY: number;
    isDragging: boolean;
  } = {
    element: null,
    startX: 0,
    startY: 0,
    isDragging: false,
  };

  constructor(scopeElement: Element) {
    this.scopeElement = scopeElement;
  }

  // 设置数据，触发更新
  setData(data: DataObject): void {
    this.data = { ...this.data, ...data };
    this.updateText();
  }

  // 获取数据
  getData(key?: string): DataValue {
    return key != undefined ? this.getDataValue(key) : this.data;
  }

  insert(str: string, position: InsertPosition = "beforeend"): void {
    this.scopeElement.insertAdjacentHTML(position, str);
    this.updateText();
    this.bindClickEvent();
  }

  // 在insert插入的内容中，将{this.xxx}替换成真正的值，并且this.xxx发生变化时，自动更新页面
  updateText(): void {
    this.processTextNodes(this.scopeElement);
  }

  private processTextNodes(element: Element | Node): void {
    // 处理元素节点的属性
    if (element instanceof Element) {
      this.processAttributes(element);
    }

    // 处理文本节点
    if (element.nodeType === Node.TEXT_NODE) {
      this.processTextContent(element as Text);
      return;
    }

    // 递归处理子节点
    const childNodes = Array.from(element.childNodes);
    for (const child of childNodes) {
      this.processTextNodes(child);
    }
  }

  private processAttributes(element: Element): void {
    // 获取该元素的所有属性模板记录
    const attributeTemplates = this.originalAttributeTemplates.get(element);

    if (attributeTemplates) {
      // 如果有保存的模板记录，处理所有需要更新的属性
      attributeTemplates.forEach((originalTemplate, attrName) => {
        if (element.hasAttribute(attrName)) {
          const newValue = this.replaceTemplate(originalTemplate);
          element.setAttribute(attrName, newValue);
        }
      });
    } else {
      // 第一次处理：查找包含模板的属性并保存
      const attributes = Array.from(element.attributes);
      const newAttributeTemplates = new Map<string, string>();

      for (const attr of attributes) {
        if (attr.value.includes("{this.")) {
          // 保存原始模板
          newAttributeTemplates.set(attr.name, attr.value);
          // 立即更新一次
          const newValue = this.replaceTemplate(attr.value);
          if (newValue !== attr.value) {
            element.setAttribute(attr.name, newValue);
          }
        }
      }

      // 如果有保存的模板，记录到 Map 中
      if (newAttributeTemplates.size > 0) {
        this.originalAttributeTemplates.set(element, newAttributeTemplates);
      }
    }

    // 清理不再存在的属性记录
    this.cleanupAttributeTemplates(element);
  }

  private cleanupAttributeTemplates(element: Element): void {
    const attributeTemplates = this.originalAttributeTemplates.get(element);
    if (attributeTemplates) {
      const attributesToRemove: string[] = [];

      attributeTemplates.forEach((originalValue, attrName) => {
        if (!element.hasAttribute(attrName)) {
          attributesToRemove.push(attrName);
        }
      });

      attributesToRemove.forEach((attrName) => {
        attributeTemplates.delete(attrName);
      });

      if (attributeTemplates.size === 0) {
        this.originalAttributeTemplates.delete(element);
      }
    }
  }

  private processTextContent(textNode: Text): void {
    const textContent = textNode.textContent;

    // 如果已经有原始模板保存，使用原始模板
    const originalTemplate = this.originalTemplates.get(textNode);
    const templateToUse = originalTemplate ?? textContent;

    if (templateToUse && templateToUse.includes("{this.")) {
      const newContent = this.replaceTemplate(templateToUse);

      // 保存原始模板（如果是第一次处理）
      if (originalTemplate == undefined && textContent) {
        this.originalTemplates.set(textNode, textContent);
      }

      if (newContent !== textNode.textContent) {
        textNode.textContent = newContent;
      }
    }
  }

  private replaceTemplate(text: string): string {
    return text.replace(
      /\{this\.([^}]+)}/g,
      (match: string, key: string): string => {
        const value = this.getDataValue(key.trim());
        return value !== undefined ? String(value) : match;
      },
    );
  }

  private getDataValue(key: string): DataValue | undefined {
    const keys = key.split(".");
    let value: DataValue = this.data;

    for (const k of keys) {
      if (
        value != undefined &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        k in value
      ) {
        value = (value as DataObject)[k];
      } else {
        return undefined;
      }
    }
    return value;
  }

  // 在insert插入的内容中,如<div onClick="this.log(1)"></div>,将onClick绑定的事件正确解析
  // 参数支持 字符串，数值，布尔值等常用的变量类型，支持$event传入当前点击事件的event
  bindClickEvent(): void {
    const elements = this.scopeElement.querySelectorAll("[hz-click]");

    elements.forEach((element: Element): void => {
      const onClickAttr = element.getAttribute("hz-click");
      if (onClickAttr != null) {
        // 移除原有属性，避免重复绑定
        element.removeAttribute("hz-click");

        // 清理之前的事件处理器
        const oldHandler = this.eventHandlers.get(element);
        if (oldHandler) {
          element.removeEventListener("click", oldHandler);
          element.removeEventListener(
            "mousedown",
            this.handleMouseDown.bind(this) as EventListener,
          );
          element.removeEventListener(
            "mousemove",
            this.handleMouseMove.bind(this) as EventListener,
          );
          this.eventHandlers.delete(element);
        }

        // 创建新的事件处理器
        const handler: EventHandler = (event: Event): void => {
          if (this.dragState.isDragging && this.dragState.element === element) {
            this.dragState.isDragging = false;
            this.dragState.element = null;
            return;
          }
          this.executeClickHandler(onClickAttr, event);
        };

        // 绑定鼠标事件来检测拖拽
        element.addEventListener(
          "mousedown",
          this.handleMouseDown.bind(this) as EventListener,
        );
        element.addEventListener(
          "mousemove",
          this.handleMouseMove.bind(this) as EventListener,
        );
        element.addEventListener("click", handler, false);
        this.eventHandlers.set(element, handler);
      }
    });
  }

  // 鼠标按下事件处理
  private handleMouseDown(event: MouseEvent): void {
    const element = event.currentTarget as Element;
    this.dragState = {
      element,
      startX: event.clientX,
      startY: event.clientY,
      isDragging: false,
    };
  }

  // 鼠标移动事件处理
  private handleMouseMove(event: MouseEvent): void {
    if (!this.dragState.element) {
      return;
    }

    const moveX = Math.abs(event.clientX - this.dragState.startX);
    const moveY = Math.abs(event.clientY - this.dragState.startY);

    // 如果移动距离超过阈值，认为是拖拽操作
    const dragThreshold = 5; // 5像素阈值
    if (moveX > dragThreshold || moveY > dragThreshold) {
      this.dragState.isDragging = true;
    }
  }

  private executeClickHandler(handlerString: string, event: Event): void {
    try {
      const match = handlerString.match(/^this\.(\w+)\(([^)]*)\)$/);
      if (!match) {
        console.warn(`Invalid click handler format: ${handlerString}`);
        return;
      }

      const methodName = match[1] as keyof this;
      const argsString: string = match[2];

      const args: DataValue[] = this.parseArguments(argsString);
      const processedArgs: DataValue[] = this.processEventArguments(
        args,
        event,
      );
      const fn = this[methodName];
      if (typeof fn === "function") {
        fn.apply(this, processedArgs);
      }
    } catch (error) {
      console.error("Error executing click handler:", error);
    }
  }

  private processEventArguments(args: DataValue[], event: Event): DataValue[] {
    return args.map((arg: DataValue): DataValue => {
      return arg === "$event" ? event : arg;
    });
  }

  private parseArguments(argsString: string): DataValue[] {
    const trimmed = argsString.trim();
    if (!trimmed) {
      return [];
    }

    const args: DataValue[] = [];
    let current: string = "";
    let inString: boolean = false;
    let stringChar: string = "";
    let parentheses: number = 0;
    let brackets: number = 0;
    let braces: number = 0;

    for (let i = 0; i < trimmed.length; i++) {
      const char: string = trimmed[i];

      if (!inString) {
        if (char === '"' || char === "'") {
          inString = true;
          stringChar = char;
          current += char;
          continue;
        } else if (char === "(") {
          parentheses++;
        } else if (char === ")") {
          parentheses--;
        } else if (char === "[") {
          brackets++;
        } else if (char === "]") {
          brackets--;
        } else if (char === "{") {
          braces++;
        } else if (char === "}") {
          braces--;
        } else if (
          char === "," &&
          parentheses === 0 &&
          brackets === 0 &&
          braces === 0
        ) {
          args.push(this.parseValue(current.trim()));
          current = "";
          continue;
        }
      } else {
        if (char === stringChar && trimmed[i - 1] !== "\\") {
          inString = false;
          stringChar = "";
        }
      }

      current += char;
    }

    if (current.trim()) {
      args.push(this.parseValue(current.trim()));
    }

    return args;
  }

  private parseValue(value: string): DataValue {
    const trimmed = value.trim();

    // 处理变量引用 {this.xxx}
    const variableMatch = trimmed.match(/^\{\s*this\.([^}]+)\s*}$/);
    if (variableMatch) {
      const key = variableMatch[1].trim(); // 提取键名并去除前后空格
      const dataValue = this.getDataValue(key);
      return dataValue !== undefined ? dataValue : trimmed;
    }

    // 处理字符串
    if (
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))
    ) {
      return trimmed.slice(1, -1).replace(/\\'/g, "'").replace(/\\"/g, '"');
    }

    // 处理数字
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return Number(trimmed);
    }

    // 处理布尔值
    if (trimmed === "true") {
      return true;
    }
    if (trimmed === "false") {
      return false;
    }

    // 处理 null
    if (trimmed === "null") {
      return null;
    }

    // 处理 undefined
    if (trimmed === "undefined") {
      return undefined;
    }

    // 处理 $event 特殊变量
    if (trimmed === "$event") {
      return "$event";
    }

    // 处理对象和数组
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        return this.validateParsedValue(parsed);
      } catch {
        return trimmed;
      }
    }

    // 默认返回原字符串
    return trimmed;
  }

  private validateParsedValue(value: unknown): DataValue {
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      value === undefined
    ) {
      return value as DataValue;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.validateParsedValue(item)) as DataArray;
    }

    if (typeof value === "object") {
      const obj: DataObject = {};
      for (const [key, val] of Object.entries(value)) {
        obj[key] = this.validateParsedValue(val);
      }
      return obj;
    }

    return String(value);
  }

  // 清理方法，避免内存泄漏
  destroy(): void {
    this.eventHandlers.forEach(
      (handler: EventHandler, element: Element): void => {
        element.removeEventListener("click", handler);
      },
    );
    this.eventHandlers.clear();
    // 清理所有模板缓存
    this.originalTemplates.clear();
    this.originalAttributeTemplates.clear();
  }
}
